// Tail-spend engine — pure reducer over Workspace.
//
// The lifecycle FSM (lifecycle.ts) handles per-deal transitions. This file
// adds the cross-deal effects: writing audit events, decision resolutions,
// notifications, etc.

import { transition, DealEvent } from "./lifecycle";
import {
    AuditEvent,
    Deal,
    DealId,
    DecisionOptionKind,
    DecisionResolution,
    Tone,
    Workspace,
} from "./types";
import { dealBadge, dealResultLabel } from "./api";

export type WorkspaceEvent =
    | { type: "HumanDecision"; dealId: DealId; option: DecisionOptionKind; optionTitle: string; byName: string }
    | { type: "MarkNotificationsRead" }
    | { type: "PlayScriptedEvent"; dealId: DealId; event: DealEvent }
    | { type: "SendDirectMessage"; dealId: DealId; byName: string; preview: string }
    | { type: "ToggleManualPause"; dealId: DealId; byName: string };

let auditCounter = 1_000_000;
const newAuditId = () => `ae-${auditCounter++}`;

function updateDeal(
    workspace: Workspace,
    dealId: DealId,
    fn: (d: Deal) => Deal
): Workspace {
    return {
        ...workspace,
        deals: workspace.deals.map((d) => (d.id === dealId ? fn(d) : d)),
    };
}

export function apply(workspace: Workspace, event: WorkspaceEvent): Workspace {
    switch (event.type) {
        case "HumanDecision": {
            const deal = workspace.deals.find((d) => d.id === event.dealId);
            if (!deal || !deal.escalation) return workspace;

            const tr = transition(deal.state, {
                type: "HumanDecision",
                option: event.option,
            });
            if (!tr.ok) return workspace;

            const chosenOpt = deal.escalation.decision.options.find(
                (o) => o.kind === event.option
            );
            const resolution: DecisionResolution = {
                chosenOptionId: chosenOpt?.id ?? event.option,
                chosenOptionTitle: event.optionTitle,
                decidedByName: event.byName,
                decidedAtLabel: "Just now",
                result: pickResult(event.option),
                supplierName: deal.supplierName,
                issue: deal.escalation.title,
                resultTone: pickResultTone(event.option),
                dealId: deal.id,
            };

            const humanEntry: AuditEvent = {
                id: newAuditId(),
                actor: "human",
                whenLabel: "Just now",
                event: "Decision recorded",
                detail: `${event.byName} chose: ${event.optionTitle}`,
            };
            const agentNext = pickAgentNext(event.option);
            const agentEntry: AuditEvent = {
                id: newAuditId(),
                actor: "agent",
                whenLabel: "Just now",
                event: agentNext.event,
                detail: agentNext.detail,
            };

            const next = updateDeal(workspace, deal.id, (d) => ({
                ...d,
                state: tr.next,
                escalation: { ...d.escalation!, resolution },
                statusChip: dealBadge(tr.next, d),
                resultLabel: dealResultLabel(tr.next, d),
                auditTrail: [...d.auditTrail, humanEntry, agentEntry],
            }));

            return {
                ...next,
                decisionHistory: [resolution, ...next.decisionHistory],
                notifications: next.notifications.map((n) =>
                    n.dealId === deal.id ? { ...n, read: true } : n
                ),
            };
        }
        case "MarkNotificationsRead":
            return {
                ...workspace,
                notifications: workspace.notifications.map((n) => ({
                    ...n,
                    read: true,
                })),
            };
        case "PlayScriptedEvent": {
            const deal = workspace.deals.find((d) => d.id === event.dealId);
            if (!deal) return workspace;
            const tr = transition(deal.state, event.event);
            if (!tr.ok) return workspace;
            return updateDeal(workspace, deal.id, (d) => ({
                ...d,
                state: tr.next,
                statusChip: dealBadge(tr.next, d),
                resultLabel: dealResultLabel(tr.next, d),
            }));
        }
        case "ToggleManualPause": {
            const deal = workspace.deals.find((d) => d.id === event.dealId);
            if (!deal) return workspace;
            const nowPaused = !deal.manualPause;
            const entry: AuditEvent = {
                id: newAuditId(),
                actor: "human",
                whenLabel: "Just now",
                event: nowPaused ? "Negotiation paused" : "Negotiation resumed",
                detail: `${event.byName} ${
                    nowPaused
                        ? "paused autonomous handling on this deal"
                        : "resumed autonomous handling"
                }`,
            };
            return updateDeal(workspace, deal.id, (d) => ({
                ...d,
                manualPause: nowPaused,
                auditTrail: [...d.auditTrail, entry],
            }));
        }
        case "SendDirectMessage": {
            const deal = workspace.deals.find((d) => d.id === event.dealId);
            if (!deal) return workspace;
            const trimmed = event.preview.trim();
            const snippet =
                trimmed.length > 80 ? `${trimmed.slice(0, 77)}…` : trimmed;
            const entry: AuditEvent = {
                id: newAuditId(),
                actor: "human",
                whenLabel: "Just now",
                event: "Direct message sent",
                detail: `${event.byName} contacted ${deal.supplierName}${
                    snippet ? ` · "${snippet}"` : ""
                }`,
            };
            return updateDeal(workspace, deal.id, (d) => ({
                ...d,
                auditTrail: [...d.auditTrail, entry],
            }));
        }
    }
}

function pickResult(option: DecisionOptionKind): string {
    switch (option) {
        case "adjust":
            return "Envelope adjusted · agent resuming";
        case "trade":
            return "Term trade authorized · agent resuming";
        case "take":
            return "Manual takeover · you own the thread";
        case "walk":
            return "Walked · queued for re-sourcing";
        case "reassign":
            return "Reassigned to strategic team";
        case "keep":
            return "Cap override · agent resuming";
    }
}

function pickAgentNext(option: DecisionOptionKind): { event: string; detail: string } {
    switch (option) {
        case "adjust":
            return {
                event: "Resuming with updated envelope",
                detail: "New bounds applied · drafting counter to supplier within the adjusted ceiling",
            };
        case "trade":
            return {
                event: "Authorized to trade terms",
                detail: "Drafting counter that swaps the breached lever for a within-envelope concession",
            };
        case "take":
            return {
                event: "Standing down · manual takeover",
                detail: "Agent paused on this thread · the next reply will come from you",
            };
        case "walk":
            return {
                event: "Closing thread",
                detail: "Sending decline notice · supplier queued for re-sourcing by Tactical",
            };
        case "reassign":
            return {
                event: "Handing off to strategic team",
                detail: "Full playbook attached · no further autonomous action",
            };
        case "keep":
            return {
                event: "Cap override accepted · resuming",
                detail: "Autonomous handling continues despite value above cap",
            };
    }
}

function pickResultTone(option: DecisionOptionKind): Tone {
    switch (option) {
        case "adjust":
        case "trade":
        case "take":
        case "keep":
            return "teal";
        case "reassign":
            return "slate";
        case "walk":
            return "amber";
    }
}
